import { zodResolver } from "@hookform/resolvers/zod";
import { EyeIcon, EyeOffIcon, LockKeyholeIcon, UserIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import type { z } from "zod";
import { SubmitButton } from "@/components/buttons/submit-button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { useSession } from "@/hooks/use-session";
import { useLoginMutation } from "@/redux/services/order-portal";
import { loginSchema } from "@/shared/schemas/login";

export const LoginForm = ({
  isOpen,
  onOpenChange,
  className,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  className?: string;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setSession: setSessionContext } = useSession();
  const [isVisible, setIsVisible] = useState(false);

  const [login] = useLoginMutation();

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      identifier: "",
      password: "",
    },
  });

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
  }, [isOpen]);

  const onSubmit: SubmitHandler<z.infer<typeof loginSchema>> = async (data) => {
    try {
      const loginResponse = await login({
        identifier: data.identifier,
        password: data.password,
      });
      if (loginResponse.error) {
        throw new Error("Failed to login");
      }

      const session = loginResponse.data;

      setSessionContext(session);

      onOpenChange(false);
      form.reset();
      navigate(0);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "An unknown error occurred"
      );
    }
  };

  return (
    <Form {...form}>
      <form className={className} onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="identifier"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="sr-only">{`${t("labels.username")} / ${t("labels.email")}`}</FormLabel>
              <FormControl>
                <InputGroup>
                  <InputGroupAddon>
                    <UserIcon />
                  </InputGroupAddon>
                  <InputGroupInput
                    placeholder={`${t("labels.username")} / ${t("labels.email")}*`}
                    {...field}
                  />
                </InputGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="sr-only">{t("labels.password")}</FormLabel>
              <FormControl>
                <InputGroup>
                  <InputGroupAddon>
                    <LockKeyholeIcon />
                  </InputGroupAddon>
                  <InputGroupInput
                    placeholder={`${t("labels.password")}*`}
                    type={isVisible ? "text" : "password"}
                    {...field}
                  />
                  <InputGroupAddon align="inline-end">
                    <InputGroupButton
                      onClick={() => setIsVisible(!isVisible)}
                      size="icon-sm"
                      type="button"
                      variant="ghost"
                    >
                      {isVisible ? <EyeOffIcon /> : <EyeIcon />}
                      <span className="sr-only">
                        {isVisible ? "Hide password" : "Show password"}
                      </span>
                    </InputGroupButton>
                  </InputGroupAddon>
                </InputGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <SubmitButton
          className="w-full"
          isSubmitting={form.formState.isSubmitting}
        >
          {t("labels.login")}
        </SubmitButton>
      </form>
    </Form>
  );
};
